import queryString from 'query-string';

export const isTruthyValue = (value?: string | (string | null)[] | null) => {
  const _value =
    typeof value === 'string' ? value.toLowerCase() : value?.[0]?.toLowerCase();

  return ['yes', 'true', 'y'].includes(_value ?? '');
};

export const getParsedQueryString = () => {
  return queryString.parse(window.location.search, {
    arrayFormat: 'comma',
  });
};

// Convert a key to kebab-case and lowercase without using regex
export const toKebabCase = (str: string): string => {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    if (char === ' ') {
      // Replace spaces with hyphens
      result += '-';
    } else if (char >= 'A' && char <= 'Z') {
      if (i > 0) {
        result += '-';
      }
      result += char.toLowerCase();
    } else {
      // Otherwise, just add the character
      result += char;
    }
  }
  return result.toLowerCase();
};

/**
 * URL param that selects the Actions table view ('entity' = "Grouped by
 * entity"; absent = "By individual finding"). It isn't a filter, so
 * `updateQueryString` carries it over instead of dropping it.
 */
export const TABLE_VIEW_PARAM = 'view';
export type TableView = 'issue' | 'entity';

export const readTableViewParam = (): TableView =>
  new URLSearchParams(window.location.search).get(TABLE_VIEW_PARAM) === 'entity'
    ? 'entity'
    : 'issue';

export const writeTableViewParam = (view: TableView) => {
  const params = new URLSearchParams(window.location.search);
  if (view === 'entity') {
    params.set(TABLE_VIEW_PARAM, 'entity');
  } else {
    params.delete(TABLE_VIEW_PARAM);
  }
  const query = params.toString();
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}`,
  );
};

export const updateQueryString = (queryObject: any) => {
  const parsedUrl = queryString.parseUrl(window.location.href);
  const url = parsedUrl.url;

  // Convert all keys to kebab-case and lowercase, and format values
  const formattedQueryObject: Record<string, any> = {};
  for (const [key, value] of Object.entries(queryObject)) {
    if (value !== undefined) {
      const formattedKey = toKebabCase(key);

      // Handle array values
      if (Array.isArray(value)) {
        // Format each item in the array
        formattedQueryObject[formattedKey] = value.map(item => {
          if (typeof item === 'string') {
            let result = '';
            for (let i = 0; i < item.length; i++) {
              const char = item[i];
              if (char === ' ') {
                result += '-';
              } else {
                result += char.toLowerCase();
              }
            }
            return result;
          }
          return item;
        });
      }
      // Handle string values
      else if (
        typeof value === 'string' &&
        formattedKey === 'measurement-checks-exclude'
      ) {
        const formattedValues = value
          .split(',')
          .map(item => {
            let result = '';
            for (let i = 0; i < item.length; i++) {
              const char = item[i];
              if (char === ' ') {
                result += '-';
              } else {
                result += char.toLowerCase();
              }
            }
            return result;
          })
          .join(',');

        formattedQueryObject[formattedKey] = formattedValues;
      }
      // For other string values or non-string values
      else {
        formattedQueryObject[formattedKey] = value;
      }
    }
  }

  // Carry over non-filter params that would otherwise be dropped.
  const currentTableView = new URLSearchParams(window.location.search).get(
    TABLE_VIEW_PARAM,
  );
  if (currentTableView && !(TABLE_VIEW_PARAM in formattedQueryObject)) {
    formattedQueryObject[TABLE_VIEW_PARAM] = currentTableView;
  }

  // Create the query string
  const newQueryString = `?${queryString.stringify(formattedQueryObject, {
    arrayFormat: 'comma',
  })}`;

  // Avoid unnecessary updates if the query string hasn't changed
  if (window.location.search === newQueryString) {
    return;
  }

  // Update URL
  if (window.location.search === '') {
    window.history.replaceState({}, '', `${url}${newQueryString}`);
  } else {
    window.history.pushState({}, '', `${url}${newQueryString}`);
  }

  // Trigger a custom event so other components can react to URL changes
  const event = new CustomEvent('queryStringUpdated', {
    detail: { queryObject: formattedQueryObject },
  });
  window.dispatchEvent(event);
};
